<template>
  <b-modal
    v-model="show"
    size="lg"
    scrollable
    :title="'Criar ' + $gui.batchesLC + ' em série'"
    ok-title="Criar"
    cancel-title="Cancelar"
    :ok-disabled="!canSubmit || sending"
    @ok.prevent="submit"
  >
    <b-alert :show="error !== ''" variant="danger">{{ error }}</b-alert>

    <b-form-group description="Exemplo: Mesa">
      <p class="mb-2 font-weight-bold">Nome base</p>
      <b-form-input type="text" v-model.trim="form.base_name" required></b-form-input>
    </b-form-group>

    <b-row>
      <b-col>
        <b-form-group>
          <p class="mb-2 font-weight-bold">Número inicial</p>
          <b-form-input type="number" min="1" step="1" v-model.number="form.first_number"></b-form-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group>
          <p class="mb-2 font-weight-bold">Número final</p>
          <b-form-input type="number" min="1" step="1" v-model.number="form.last_number"></b-form-input>
        </b-form-group>
      </b-col>
    </b-row>

    <b-form-group>
      <p class="mb-2 font-weight-bold">Valor de cada {{ $gui.batchLC }}</p>
      <b-input-group prepend="R$">
        <money class="form-control" v-model="form.value" v-bind="money"></money>
      </b-input-group>
    </b-form-group>

    <b-form-group v-if="showCategory">
      <p class="mb-2 font-weight-bold">Categoria</p>
      <b-form-select v-model="form.category_id" :options="categoryOptions"></b-form-select>
    </b-form-group>

    <b-form-group v-if="$gui.isEvento">
      <p class="mb-2 font-weight-bold">Limite máximo de validações</p>
      <b-form-select v-model.number="form.max_validations" :options="maxValidationsOptions"></b-form-select>
    </b-form-group>

    <b-form-group>
      <p class="mb-2 font-weight-bold">Estoque de cada {{ $gui.batchLC }}</p>
      <b-form-checkbox v-model="unlimitedStock" switch>
        {{ unlimitedStock ? 'Estoque ilimitado ativado' : 'Estoque ilimitado desativado' }}
      </b-form-checkbox>
      <b-input-group v-if="!unlimitedStock" append="unidades" class="mt-3">
        <b-form-input type="number" min="0" step="1" v-model.number="form.stock"></b-form-input>
      </b-input-group>
    </b-form-group>

    <b-form-group :description="'A Loja Virtual só aceita compras de cada ' + $gui.batchLC + ' em múltiplos deste número. Use 1 para permitir qualquer quantidade.'">
      <p class="mb-2 font-weight-bold">Mínimo de unidades por venda</p>
      <b-form-input type="number" min="1" max="30" step="1" v-model.number="form.min_sale_amount"></b-form-input>
    </b-form-group>

    <b-form-group v-if="dealerOptions.length > 0">
      <p class="mb-2 font-weight-bold">Ativar para:</p>
      <b-form-checkbox class="mb-2 text-primary" v-model="allDealers" @change="toggleAllDealers">Marcar todos</b-form-checkbox>
      <b-form-checkbox-group stacked v-model="form.dealers" :options="dealerOptions" @input="allDealers = form.dealers.length === dealerOptions.length"></b-form-checkbox-group>
    </b-form-group>

    <div v-if="problem" class="text-danger mb-2">{{ problem }}</div>
    <div v-if="conflicts.length > 0" class="text-danger mb-2">Já existem: {{ conflicts.join(', ') }}</div>

    <div v-if="names.length > 0 && names.length <= 200">
      <p class="mb-1 font-weight-bold">Serão criados {{ names.length }} {{ $gui.batchesLC }}:</p>
      <div class="border rounded p-2" style="max-height: 160px; overflow-y: auto;">
        <span v-for="name in names" :key="name" class="mr-2 d-inline-block" :class="{'text-danger font-weight-bold': isConflict(name)}">{{ name }}</span>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {Money} from 'v-money'

const normalize = (str) => String(str).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()

export default {
  name: 'BatchSeriesModal',
  components: { Money },
  props: {
    visible: { type: Boolean, default: false },
    event: { type: Object, default: () => ({}) },
    organizer: { type: String, default: '' },
    categories: { type: Array, default: () => [] }
  },
  data () {
    return {
      form: this.emptyForm(),
      unlimitedStock: true,
      allDealers: false,
      sending: false,
      error: '',
      money: { decimal: ',', thousands: '.', prefix: '', suffix: '', precision: 2 },
      maxValidationsOptions: [
        { value: 1, text: '1 - Uma vez' },
        { value: 2, text: '2 - Duas vezes' },
        { value: 3, text: '3 - Três vezes' },
        { value: 4, text: '4 - Quatro vezes' },
        { value: 5, text: '5 - Cinco vezes' }
      ]
    }
  },
  computed: {
    show: {
      get () {
        return this.visible
      },
      set (value) {
        if (!value) {
          this.$emit('close')
        }
      }
    },
    dealerOptions () {
      const dealers = (this.event && this.event.dealers) || []
      const online = dealers.filter(d => d.type === 'online').map(d => ({ value: d.id, text: 'Loja Virtual' }))
      const others = dealers.filter(d => d.type !== 'online').map(d => ({ value: d.id, text: d.name }))
      return online.concat(others)
    },
    showCategory () {
      return !!this.event && this.event.categories_enabled === 1 && this.categories.length > 0
    },
    categoryOptions () {
      const opts = [{ value: null, text: 'Sem categoria' }]
      for (const c of this.categories) {
        opts.push({ value: c.id, text: c.emoji ? c.emoji + ' ' + c.category : c.category })
      }
      return opts
    },
    names () {
      const first = this.form.first_number
      const last = this.form.last_number
      const base = this.form.base_name
      if (!Number.isInteger(first) || !Number.isInteger(last) || base === '' || last < first || last - first + 1 > 200) {
        return []
      }
      const out = []
      for (let n = first; n <= last; n++) {
        out.push(base + ' ' + n)
      }
      return out
    },
    existing () {
      return new Set(((this.event && this.event.batches) || []).map(b => normalize(b.name)))
    },
    conflicts () {
      return this.names.filter(n => this.isConflict(n))
    },
    problem () {
      const f = this.form
      if (f.base_name === '') return 'Informe o nome base.'
      if (!Number.isInteger(f.first_number) || !Number.isInteger(f.last_number) || f.first_number < 1 || f.last_number < 1) {
        return 'Os números inicial e final devem ser inteiros a partir de 1.'
      }
      if (f.last_number < f.first_number) return 'O número final deve ser maior ou igual ao inicial.'
      if (f.last_number - f.first_number + 1 > 200) return 'Uma série pode ter no máximo 200 ' + this.$gui.batchesLC
      if (!this.unlimitedStock && !(Number.isInteger(f.stock) && f.stock >= 0)) return 'O estoque deve ser um número inteiro maior ou igual a 0.'
      if (!(Number.isInteger(f.min_sale_amount) && f.min_sale_amount >= 1 && f.min_sale_amount <= 30)) {
        return 'O mínimo de unidades por venda deve ser um número inteiro de 1 a 30.'
      }
      return ''
    },
    canSubmit () {
      return this.problem === '' && this.conflicts.length === 0
    }
  },
  watch: {
    visible (open) {
      if (open) {
        this.form = this.emptyForm()
        this.unlimitedStock = true
        this.allDealers = false
        this.error = ''
        this.sending = false
      }
    }
  },
  methods: {
    emptyForm () {
      return {
        base_name: '',
        first_number: 1,
        last_number: null,
        value: 0,
        max_validations: 1,
        stock: null,
        min_sale_amount: 1,
        category_id: null,
        dealers: []
      }
    },
    isConflict (name) {
      return this.existing.has(normalize(name))
    },
    toggleAllDealers (checked) {
      this.form.dealers = checked ? this.dealerOptions.map(d => d.value) : []
    },
    submit () {
      if (!this.canSubmit) return
      const body = {
        base_name: this.form.base_name,
        first_number: this.form.first_number,
        last_number: this.form.last_number,
        value: Math.round(this.form.value * 100),
        max_validations: this.form.max_validations,
        stock: this.unlimitedStock ? null : this.form.stock,
        min_sale_amount: this.form.min_sale_amount
      }
      if (this.form.category_id !== null) {
        body.category_id = this.form.category_id
      }
      if (this.form.dealers.length > 0) {
        body.dealers = this.form.dealers
      }
      this.sending = true
      this.error = ''
      const url = process.env.VUE_APP_URL_APP + '/b/organizers/' + this.organizer + '/batch-series'
      this.$http.post(url, body).then(res => {
        this.sending = false
        this.$emit('created', res.body)
        this.$emit('close')
      }, err => {
        this.sending = false
        this.error = (err.body && err.body.message) || 'Não foi possível criar a série.'
      })
    }
  }
}
</script>
